import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  Search,
  Database,
  RefreshCw,
  ArrowUpDown,
  Lock,
  Menu,
  ChevronLeft,
  FileText
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientPortal() {
  const { accessToken } = useParams();
  const [portal, setPortal] = useState(null);
  const [pages, setPages] = useState([]);
  const [activePage, setActivePage] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [offset, setOffset] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    fetchPortal();
  }, [accessToken]);

  useEffect(() => {
    if (activePage) {
      fetchRecords();
    }
  }, [activePage]);

  const fetchPortal = async () => {
    try {
      const response = await axios.get(`${API}/client/portal/${accessToken}`);
      setPortal(response.data);
      setPages(response.data.pages || []);
      if (response.data.pages?.length > 0) {
        setActivePage(response.data.pages[0]);
      }
    } catch (error) {
      setError("Invalid or expired access link");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecords = async (newOffset = null) => {
    if (!activePage) return;
    setRecordsLoading(true);
    try {
      const response = await axios.post(`${API}/client/portal/${accessToken}/page/${activePage.page_id}/records`, {
        table_name: activePage.table_name,
        page_size: 50,
        offset: newOffset
      });
      
      const newRecords = response.data.records || [];
      setRecords(newOffset ? [...records, ...newRecords] : newRecords);
      setOffset(response.data.offset || null);
      setHasMore(!!response.data.offset);
    } catch (error) {
      toast.error("Failed to load records");
    } finally {
      setRecordsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setActivePage(page);
    setRecords([]);
    setOffset(null);
    setSearchTerm("");
    setSortField(null);
  };

  const handleLoadMore = () => {
    if (offset && !recordsLoading) {
      fetchRecords(offset);
    }
  };

  const handleRefresh = () => {
    setOffset(null);
    fetchRecords();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const fields = useMemo(() => {
    const fieldSet = new Set();
    records.forEach((record) => {
      Object.keys(record.fields || {}).forEach((key) => fieldSet.add(key));
    });
    return Array.from(fieldSet);
  }, [records]);

  const filteredRecords = useMemo(() => {
    let result = [...records];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((record) =>
        Object.values(record.fields || {}).some((value) =>
          String(value).toLowerCase().includes(term)
        )
      );
    }

    if (sortField) {
      result.sort((a, b) => {
        const aVal = a.fields?.[sortField] ?? "";
        const bVal = b.fields?.[sortField] ?? "";
        const comparison = String(aVal).localeCompare(String(bVal));
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }

    return result;
  }, [records, searchTerm, sortField, sortDirection]);

  // Clean up Airtable linked record format ("0, Value" -> "Value")
  const cleanLinkedRecordValue = (value) => {
    if (typeof value !== 'string') return value;
    const match = value.match(/^\d+,\s*(.+)$/);
    if (match) return match[1];
    return value;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") return JSON.stringify(value);
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return cleanLinkedRecordValue(String(value));
  };

  const primaryColor = portal?.branding?.primary_color || "#2CC295";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020807] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#020807] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#032221] border border-[#03624C] rounded-lg flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-[#F1F7F6]/40" />
          </div>
          <h1 className="text-2xl font-bold text-[#F1F7F6] mb-2">Access Denied</h1>
          <p className="text-[#F1F7F6]/60">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020807] flex">
      {/* Sidebar */}
      <aside 
        className={`${sidebarOpen ? 'w-64' : 'w-0'} bg-[#032221] border-r border-[#03624C] flex-shrink-0 transition-all duration-300 overflow-hidden`}
      >
        <div className="w-64 h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-[#03624C]" style={{ background: `linear-gradient(to right, ${primaryColor}10, transparent)` }}>
            <div className="flex items-center gap-2 mb-2">
              {portal?.branding?.logo_url ? (
                <img src={portal.branding.logo_url} alt="Logo" className="h-6" />
              ) : (
                <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                  <Database className="w-4 h-4 text-[#020807]" />
                </div>
              )}
              <span className="font-bold text-[#F1F7F6] truncate">
                {portal?.branding?.company_name || portal?.name}
              </span>
            </div>
            {portal?.description && (
              <p className="text-xs text-[#F1F7F6]/60 truncate">{portal.description}</p>
            )}
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-2 overflow-y-auto">
            <p className="text-xs uppercase tracking-wider text-[#F1F7F6]/40 px-3 py-2">Pages</p>
            {pages.length === 0 ? (
              <p className="text-sm text-[#F1F7F6]/40 px-3 py-2">No pages available</p>
            ) : (
              <ul className="space-y-1">
                {pages.map((page) => (
                  <li key={page.page_id}>
                    <button
                      onClick={() => handlePageChange(page)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                        activePage?.page_id === page.page_id
                          ? 'text-[#F1F7F6] font-medium'
                          : 'text-[#F1F7F6]/70 hover:bg-[#03624C]/30'
                      }`}
                      style={activePage?.page_id === page.page_id ? { backgroundColor: `${primaryColor}20`, color: primaryColor } : {}}
                    >
                      <FileText className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{page.nav_title || page.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="border-b border-[#03624C] sticky top-0 z-40 bg-[#020807]">
          <div className="px-4 md:px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="text-[#F1F7F6]/60 hover:text-[#F1F7F6]"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              <div>
                <h1 className="text-lg font-bold text-[#F1F7F6]">
                  {activePage?.nav_title || activePage?.name || "Select a page"}
                </h1>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-[#F1F7F6]/60 hover:text-[#F1F7F6]"
              onClick={handleRefresh}
              disabled={recordsLoading || !activePage}
            >
              <RefreshCw className={`w-5 h-5 ${recordsLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          {!activePage ? (
            <div className="text-center py-24">
              <FileText className="w-12 h-12 text-[#F1F7F6]/20 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#F1F7F6] mb-2">No page selected</h3>
              <p className="text-[#F1F7F6]/60">Select a page from the sidebar</p>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F1F7F6]/40" />
                  <Input
                    className="pl-10 bg-[#032221] border-[#03624C] text-[#F1F7F6] placeholder:text-[#F1F7F6]/30"
                    placeholder="Search records..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ borderColor: searchTerm ? primaryColor : undefined }}
                  />
                </div>
                <div className="flex items-center gap-2 text-sm text-[#F1F7F6]/60">
                  <span>{filteredRecords.length} records</span>
                </div>
              </div>

              {/* Table */}
              <div className="bg-[#032221] border border-[#03624C] rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr style={{ backgroundColor: `${primaryColor}20` }}>
                        {fields.map((field) => (
                          <th
                            key={field}
                            className="px-4 py-3 text-left text-xs font-bold text-[#F1F7F6] uppercase tracking-wider cursor-pointer hover:bg-white/5 transition-colors"
                            onClick={() => handleSort(field)}
                          >
                            <div className="flex items-center gap-2">
                              <span>{field}</span>
                              {sortField === field && (
                                <ArrowUpDown className={`w-3 h-3 ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {recordsLoading && records.length === 0 ? (
                        <tr>
                          <td colSpan={fields.length || 1} className="px-4 py-12 text-center">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: primaryColor }} />
                          </td>
                        </tr>
                      ) : filteredRecords.length === 0 ? (
                        <tr>
                          <td colSpan={fields.length || 1} className="px-4 py-12 text-center text-[#F1F7F6]/40">
                            {searchTerm ? "No matching records found" : "No records available"}
                          </td>
                        </tr>
                      ) : (
                        filteredRecords.map((record) => (
                          <tr
                            key={record.id}
                            className="border-t border-[#03624C]/50 hover:bg-[#03624C]/20 transition-colors"
                          >
                            {fields.map((field) => (
                              <td key={field} className="px-4 py-3 text-sm text-[#F1F7F6]">
                                {formatValue(record.fields?.[field])}
                              </td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Load More */}
              {hasMore && (
                <div className="mt-6 text-center">
                  <Button
                    variant="outline"
                    className="border-[#03624C] text-[#F1F7F6] hover:bg-[#03624C]/30"
                    onClick={handleLoadMore}
                    disabled={recordsLoading}
                  >
                    {recordsLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : "Load More Records"}
                  </Button>
                </div>
              )}
            </>
          )}
        </main>

        {/* Footer */}
        <footer className="border-t border-[#03624C]/30 py-4">
          <div className="text-center text-sm text-[#F1F7F6]/30">
            Powered by Paeora
          </div>
        </footer>
      </div>
    </div>
  );
}
